import { isAllowedOrigin, isEmbedMessage, type EmbedMessage } from "./messages";

/**
 * The iframe side of the embed channel.
 *
 * The parent's origin arrives in the iframe URL, is validated once, and is then the only origin
 * this side will ever post to or accept from.
 */

let parentOrigin: string | null = null;
let provenParentOrigin: string | null = null;

function resolveParentOrigin(): string | null {
  if (parentOrigin !== null) return parentOrigin;
  if (typeof window === "undefined") return null;
  const declared = new URLSearchParams(window.location.search).get("origin");
  parentOrigin = isAllowedOrigin(declared) ? declared : null;
  return parentOrigin;
}

export function publishEmbedEvent(message: EmbedMessage): void {
  if (typeof window === "undefined" || window.parent === window) return;
  const origin = resolveParentOrigin();
  if (!origin) return;
  window.parent.postMessage(message, origin);
}

export function proveParentOrigin(timeoutMs = 5_000): Promise<string> {
  const origin = resolveParentOrigin();
  const challenge = new URLSearchParams(window.location.search).get("origin_challenge");
  if (!origin || !challenge || challenge.length < 16 || challenge.length > 160 || window.parent === window) {
    return Promise.reject(new Error("EMBED_ORIGIN_PROOF_REQUIRED"));
  }
  if (provenParentOrigin === origin) return Promise.resolve(origin);

  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      window.removeEventListener("message", onMessage);
      reject(new Error("EMBED_ORIGIN_PROOF_TIMEOUT"));
    }, timeoutMs);
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin || event.source !== window.parent || !isEmbedMessage(event.data)) return;
      if (event.data.type !== "exodus.booking.origin_proof" || event.data.challenge !== challenge) return;
      window.clearTimeout(timeout);
      window.removeEventListener("message", onMessage);
      provenParentOrigin = origin;
      resolve(origin);
    };
    window.addEventListener("message", onMessage);
    window.parent.postMessage({ type: "exodus.booking.origin_challenge", challenge } satisfies EmbedMessage, origin);
  });
}

/**
 * Reports our own height so the host page can size the iframe.
 *
 * Uses ResizeObserver rather than a poll, and only reports whole pixels that actually changed —
 * a hotel page that reflows on every message would jitter visibly.
 */
export function startHeightSync(): () => void {
  if (typeof window === "undefined" || typeof ResizeObserver === "undefined") return () => {};

  // Measured on the app root, not on documentElement: inside an iframe the document can never
  // report less than the frame's own viewport, so a scrollHeight-based sync can only ever grow
  // the frame. The result is a booking panel with a hundred pixels of dead space under it that
  // never goes away.
  const root = document.getElementById("root") ?? document.body;

  let lastHeight = 0;
  const report = () => {
    const height = Math.ceil(root.getBoundingClientRect().height);
    if (height === 0 || height === lastHeight) return;
    lastHeight = height;
    publishEmbedEvent({ type: "exodus.booking.resize", height });
  };

  const observer = new ResizeObserver(report);
  observer.observe(root);

  const onMessage = (event: MessageEvent) => {
    const origin = resolveParentOrigin();
    if (!origin || event.origin !== origin) return;
    if (!isEmbedMessage(event.data)) return;
    if (event.data.type === "exodus.booking.open") report();
  };
  window.addEventListener("message", onMessage);

  report();
  return () => {
    observer.disconnect();
    window.removeEventListener("message", onMessage);
  };
}
